import { create } from 'zustand';
import { apiGet } from '../lib/api';

// Helpers to enrich raw services with creative details from other tables

async function fetchCreativesByIds(ids) {
  const unique = Array.from(new Set((Array.isArray(ids) ? ids : []).filter(Boolean)));
  if (unique.length === 0) return {};
  const results = await Promise.all(
    unique.map(async (id) => {
      try {
        // Use /creatives endpoint
        const payload = await apiGet(`/creatives/${id}`);
        const c = payload?.data || payload?.creative || null;
        return [id, c];
      } catch {
        return [id, null];
      }
    })
  );
  return Object.fromEntries(results);
}

async function enrichServicesWithCreative(services) {
  const arr = Array.isArray(services) ? services : [];
  // Services table uses creative_id column
  const creativeIds = Array.from(new Set(arr.map((s) => s?.creative_id).filter(Boolean)));
  const creativesById = await fetchCreativesByIds(creativeIds);
  return arr.map((s) => {
    const lookupId = s?.creative_id;
    const c = creativesById[lookupId] || null;
    const simplifiedCreative = c
      ? {
          id: c.id,
          professional_title: c.professional_title,
          verification_status: c.verification_status,
          verification_badge: c.verification_badge,
          location: c.location,
          rating: c.rating,
          total_reviews: c.total_reviews,
          user: c.user,
        }
      : null;
    const rating = c?.rating ?? s.rating;
    const reviews = c?.total_reviews ?? s.reviews;
    const location = s.location || c?.location || s.city_name || s.city || s.cityName;
    return { ...s, creative: simplifiedCreative, rating, reviews, location };
  });
}

export const useServicesStore = create((set, get) => ({
  // Core data
  items: [],
  loading: true,
  error: null,

  // Filters
  search: '',
  selectedCategory: '',
  selectedCategories: [],
  selectedLocation: '',
  selectedCountry: '',
  selectedCity: '',
  minPrice: '',
  maxPrice: '',
  minRating: '',
  sortBy: 'recommended',

  // Categories
  categoriesList: [],
  categoryMap: {},

  // Pagination
  page: 1,
  limit: 100,
  loadingMore: false,
  hasMore: true,
  // Server filter
  verifiedOnly: false,

  // Actions
  initLoad: async () => {
    try {
      set({ loading: true, error: null });
      const limit = get().limit;
      console.log('🔍 Fetching services (public)...', { page: 1, limit, verified_only: false });
      let payload = await apiGet('/services/public', { page: 1, limit, verified_only: false });
      let services = payload?.data?.services || payload?.services || (Array.isArray(payload?.data) ? payload?.data : []);
      let base = Array.isArray(services) ? services : [];
      if (base.length === 0) {
        const alt = await apiGet('/services', { page: 1, limit });
        const altList = alt?.data?.services || alt?.services || [];
        base = (Array.isArray(altList) ? altList : []).filter((s) => String(s?.visibility || '').toLowerCase() === 'public');
      }
      // Enrich with creative details (including user fields for avatar/name)
      const finalArr = await enrichServicesWithCreative(base);
      // Dedupe by id to avoid duplicate cards
      const byId = new Map();
      for (const s of finalArr) {
        const id = String(s?.id || '');
        if (!id) continue;
        if (!byId.has(id)) byId.set(id, s);
      }
      const unique = Array.from(byId.values());
      set({ items: unique, page: 2, hasMore: unique.length >= limit });
    } catch (e) {
      console.error('❌ Error loading services (store):', e);
      set({ error: 'Failed to load services' });
    } finally {
      set({ loading: false });
    }
  },

  loadMore: async () => {
    const { loadingMore, hasMore, page, limit } = get();
    if (loadingMore || !hasMore) return;
    set({ loadingMore: true });
    try {
      let payload = await apiGet('/services/public', { page, limit, verified_only: false });
      let services = payload?.data?.services || payload?.services || (Array.isArray(payload?.data) ? payload?.data : []);
      let base = Array.isArray(services) ? services : [];
      if (base.length === 0) {
        const alt = await apiGet('/services', { page, limit });
        const altList = alt?.data?.services || alt?.services || [];
        base = (Array.isArray(altList) ? altList : []).filter((s) => String(s?.visibility || '').toLowerCase() === 'public');
      }
      const next = await enrichServicesWithCreative(base);
      // Append and dedupe by id to prevent duplicates
      const combined = [...get().items, ...next];
      const byId = new Map();
      for (const s of combined) {
        const id = String(s?.id || '');
        if (!id) continue;
        if (!byId.has(id)) byId.set(id, s);
      }
      const unique = Array.from(byId.values());
      set({ items: unique, hasMore: next.length >= limit, page: page + 1 });
    } catch (e) {
      console.error('❌ Error loading more services (store):', e);
      set({ hasMore: false });
    } finally {
      set({ loadingMore: false });
    }
  },

  fetchCategories: async () => {
    try {
      const payload = await apiGet('/service-categories', { page: 1, limit: 200 });
      const cats = payload?.data?.categories || payload?.categories || [];
      const list = Array.isArray(cats) ? cats : [];
      const map = {};
      list.forEach((c) => { if (c && c.id) map[c.id] = c.name || c.title || String(c.id); });
      set({ categoriesList: list, categoryMap: map });
    } catch (e) {
      // Silent error; UI can fallback to service fields
      console.warn('⚠️ Failed to fetch categories (store):', e?.message || e);
    }
  },

  // Setters
  setSearch: (v) => set({ search: v }),
  setSelectedCategory: (v) => set({ selectedCategory: v }),
  setSelectedCategories: (arr) => set({ selectedCategories: Array.isArray(arr) ? arr : [] }),
  setSelectedLocation: (v) => set({ selectedLocation: v }),
  setSelectedCountry: (v) => set({ selectedCountry: v }),
  setSelectedCity: (v) => set({ selectedCity: v }),
  setMinPrice: (v) => set({ minPrice: v }),
  setMaxPrice: (v) => set({ maxPrice: v }),
  setMinRating: (v) => set({ minRating: v }),
  setSortBy: (v) => set({ sortBy: v }),
  setVerifiedOnly: (v) => set({ verifiedOnly: Boolean(v) }),
  resetFilters: () => set({
    search: '',
    selectedCategory: '',
    selectedCategories: [],
    selectedLocation: '',
    selectedCountry: '',
    selectedCity: '',
    minPrice: '',
    maxPrice: '',
    minRating: '',
    sortBy: 'recommended',
  }),

  // Derived selectors
  categoryOptions: () => {
    const list = get().categoriesList;
    const names = (Array.isArray(list) ? list : []).map((c) => c.name || c.title).filter(Boolean);
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  },
  locationOptions: () => {
    const items = get().items;
    const locs = new Set();
    (Array.isArray(items) ? items : []).forEach((s) => {
      const l = s.location || s.city_name || s.city || s.cityName;
      if (l) locs.add(String(l));
    });
    return Array.from(locs).sort((a, b) => a.localeCompare(b));
  },
  filteredItems: () => {
    const {
      items,
      search,
      selectedCategory,
      selectedCategories,
      selectedLocation,
      selectedCountry,
      selectedCity,
      minPrice,
      maxPrice,
      minRating,
      sortBy,
      categoryMap,
    } = get();
    let list = Array.isArray(items) ? items.slice() : [];

    // Text search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((s) => {
        const title = String(s.title || s.name || '').toLowerCase();
        const desc = String(s.description || '').toLowerCase();
        return title.includes(q) || desc.includes(q);
      });
    }

    // Category filter (supports multi-select)
    const chosenCats = new Set(
      (Array.isArray(selectedCategories) ? selectedCategories : [])
        .map((c) => String(c).toLowerCase())
        .filter(Boolean)
    );
    if (selectedCategory) chosenCats.add(String(selectedCategory).toLowerCase());
    if (chosenCats.size > 0) {
      const hasCategoryNames =
        (categoryMap && Object.keys(categoryMap).length > 0) ||
        list.some((s) => !!(s?.category || s?.subcategory || s?.mainCategory));
      if (hasCategoryNames) {
        list = list.filter((s) => {
          const mappedName = s?.category_id && categoryMap?.[s.category_id] ? categoryMap[s.category_id] : undefined;
          const names = [mappedName, s?.category, s?.subcategory, s?.mainCategory]
            .filter(Boolean)
            .map((n) => String(n).toLowerCase());
          return names.some((n) => chosenCats.has(n));
        });
      }
    }

    // Location filter: legacy text + structured country/city
    if (selectedLocation) {
      const loc = String(selectedLocation).toLowerCase();
      list = list.filter((s) =>
        String(s.location || s.city_name || s.city || s.cityName || '')
          .toLowerCase()
          .includes(loc)
      );
    }
    if (selectedCountry) {
      const ctry = String(selectedCountry).toLowerCase();
      list = list.filter((s) =>
        String(s.location || s.city_name || s.city || s.cityName || '')
          .toLowerCase()
          .includes(ctry)
      );
    }
    if (selectedCity) {
      const city = String(selectedCity).toLowerCase();
      list = list.filter((s) =>
        String(s.location || s.city_name || s.city || s.cityName || '')
          .toLowerCase()
          .includes(city)
      );
    }

    // Price range
    const toNumber = (v) => { if (v === '' || v == null) return undefined; const n = Number(v); return Number.isFinite(n) ? n : undefined; };
    const minP = toNumber(minPrice);
    const maxP = toNumber(maxPrice);
    const getEffectivePrice = (s) => {
      const pkg = s?.is_package ? (s?.package_price ?? s?.base_price) : undefined;
      const base = s?.hourly_rate ?? s?.base_price ?? s?.price;
      const val = pkg ?? base ?? 0;
      const n = Number(val);
      return Number.isFinite(n) ? n : 0;
    };
    if (minP !== undefined) list = list.filter((s) => getEffectivePrice(s) >= minP);
    if (maxP !== undefined) list = list.filter((s) => getEffectivePrice(s) <= maxP);

    // Rating
    const minR = toNumber(minRating);
    if (minR !== undefined) list = list.filter((s) => Number(s.rating ?? 0) >= minR);

    // Sorting
    switch (sortBy) {
      case 'priceLowToHigh':
        list.sort((a, b) => getEffectivePrice(a) - getEffectivePrice(b));
        break;
      case 'priceHighToLow':
        list.sort((a, b) => getEffectivePrice(b) - getEffectivePrice(a));
        break;
      case 'highestRated':
        list.sort((a, b) => Number(b.rating ?? 0) - Number(a.rating ?? 0));
        break;
      default:
        // recommended: keep original order
        break;
    }
    return list;
  },
}));
